import { OccupancyBlockCostClass } from '../../shared/models/occupancy-dashboard.model';
import { DashboardCalendar } from '../../shared/ui-models/occupancy-dashboard.ui-model';

/** The revenue management team's palette, kept as-is so the port reads like their prototype. */
export const C = {
  green: '#0E6B4F',
  mintBg: '#E7F5EF',
  clay: '#B45309',
  clayBg: '#FDF1DC',
  ink: '#1B2430',
  muted: '#6B7688',
  line: '#E4E9F0',
  page: '#F6F8FB',
  blue: '#2F6FB5',
  plum: '#6941C6'
};

export interface Tone {
  bg: string;
  fg: string;
}

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const DAY_MS = 864e5;

const startUtc = (cal: DashboardCalendar): number => {
  const [y, m, d] = cal.start.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

export const idxToDate = (cal: DashboardCalendar, i: number): Date =>
  new Date(startUtc(cal) + i * DAY_MS);

export const idxToInput = (cal: DashboardCalendar, i: number): string =>
  idxToDate(cal, i).toISOString().slice(0, 10);

export const dateToIdx = (cal: DashboardCalendar, s: string): number => {
  const [y, m, d] = s.split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - startUtc(cal)) / DAY_MS);
};

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' */
export const fmtDate = (s: string): string => s.split('-').reverse().join('/');

/** Night index → '07 Sep' */
export const fmtDay = (cal: DashboardCalendar, i: number): string => {
  const d = idxToDate(cal, i);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_ABBR[d.getUTCMonth()]}`;
};

export const fmtPct = (v: number | null | undefined, dp = 1): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${v.toFixed(dp)} %`;

export const fmtNum = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : Math.round(v).toLocaleString('en-US');

export const fmtMoney = (v: number | null | undefined): string =>
  v ? Math.round(v).toLocaleString('en-US') : '0';

export const fmtK = (v: number | null | undefined): string => {
  if (!v) return '0';
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `${Math.round(v / 1e3)}k`;
  return Math.round(v).toString();
};

export const uniq = (a: string[]): string[] => Array.from(new Set(a)).sort();

export const occTone = (v: number | null): Tone => {
  if (v === null) return { bg: '#F1F3F7', fg: C.muted };
  if (v >= 85) return { bg: C.mintBg, fg: C.green };
  if (v >= 60) return { bg: '#EAF2FB', fg: C.blue };
  if (v >= 35) return { bg: '#FEF6E7', fg: '#9A6700' };
  return { bg: '#FDECEC', fg: '#B42318' };
};

export const blockTone = (v: number | null): Tone => {
  if (v === null || v === 0) return { bg: '#F1F3F7', fg: C.muted };
  if (v >= 25) return { bg: '#FDECEC', fg: '#B42318' };
  if (v >= 10) return { bg: C.clayBg, fg: C.clay };
  return { bg: '#F5F0FF', fg: C.plum };
};

export const COST_TONE: Record<OccupancyBlockCostClass, Tone & { label: string }> = {
  operating: { bg: '#FDECEC', fg: '#B42318', label: 'Operating' },
  contract: { bg: C.mintBg, fg: C.green, label: 'Contract' },
  unclassified: { bg: '#F1F3F7', fg: C.muted, label: 'Unclassified' }
};
